import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  BatchRecordIngredient,
  BatchRecordIngredientsState,
  CreateBatchRecordIngredientRequest,
  UpdateBatchRecordIngredientRequest
} from '../../types/batchRecordIngredient';
import { batchRecordIngredientApiService } from '../../services/batchRecordIngredientApiService';

// Async thunks

/**
 * Fetch all batch record ingredients from the API
 */
export const fetchAllBatchRecordIngredients = createAsyncThunk(
  'batchRecordIngredients/fetchAll',
  async () => {
    return await batchRecordIngredientApiService.fetchAllBatchRecordIngredients();
  }
);

/**
 * Fetch batch record ingredients for a specific batch record
 */
export const fetchBatchRecordIngredientsByBatchRecordId = createAsyncThunk(
  'batchRecordIngredients/fetchByBatchRecordId',
  async (batchRecordId: string) => {
    return await batchRecordIngredientApiService.fetchBatchRecordIngredientsByBatchRecordId(batchRecordId);
  }
);

/**
 * Create a new batch record ingredient
 */
export const createBatchRecordIngredient = createAsyncThunk(
  'batchRecordIngredients/create',
  async (ingredient: CreateBatchRecordIngredientRequest) => {
    return await batchRecordIngredientApiService.createBatchRecordIngredient(ingredient);
  }
);

/**
 * Update an existing batch record ingredient
 */
export const updateBatchRecordIngredient = createAsyncThunk(
  'batchRecordIngredients/update',
  async ({ id, data }: { id: string; data: UpdateBatchRecordIngredientRequest }) => {
    return await batchRecordIngredientApiService.updateBatchRecordIngredient(id, data);
  }
);

/**
 * Delete a batch record ingredient
 */
export const deleteBatchRecordIngredient = createAsyncThunk(
  'batchRecordIngredients/delete',
  async (id: string) => {
    await batchRecordIngredientApiService.deleteBatchRecordIngredient(id);
    return id;
  }
);

// Initial state
const initialState: BatchRecordIngredientsState = {
  ingredients: [],
  loading: false,
  error: null,
};

const batchRecordIngredientsSlice = createSlice({
  name: 'batchRecordIngredients',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    // Fetch all batch record ingredients
    builder
      .addCase(fetchAllBatchRecordIngredients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllBatchRecordIngredients.fulfilled, (state, action) => {
        state.loading = false;
        state.ingredients = action.payload;
      })
      .addCase(fetchAllBatchRecordIngredients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch batch record ingredients';
      });

    // Fetch by batch record ID
    builder
      .addCase(fetchBatchRecordIngredientsByBatchRecordId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBatchRecordIngredientsByBatchRecordId.fulfilled, (state, action) => {
        state.loading = false;
        // Replace ingredients for this batch record
        const batchRecordId = action.payload[0]?.batchRecordId;
        if (batchRecordId) {
          state.ingredients = [
            ...state.ingredients.filter(i => i.batchRecordId !== batchRecordId),
            ...action.payload
          ];
        }
      })
      .addCase(fetchBatchRecordIngredientsByBatchRecordId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch batch record ingredients';
      });

    // Create batch record ingredient
    builder
      .addCase(createBatchRecordIngredient.fulfilled, (state, action) => {
        state.ingredients.push(action.payload);
      })
      .addCase(createBatchRecordIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to create batch record ingredient';
      });

    // Update batch record ingredient
    builder
      .addCase(updateBatchRecordIngredient.fulfilled, (state, action) => {
        const index = state.ingredients.findIndex(i => i.id === action.payload.id);
        if (index !== -1) {
          state.ingredients[index] = action.payload;
        }
      })
      .addCase(updateBatchRecordIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to update batch record ingredient';
      });

    // Delete batch record ingredient
    builder
      .addCase(deleteBatchRecordIngredient.fulfilled, (state, action) => {
        state.ingredients = state.ingredients.filter(i => i.id !== action.payload);
      })
      .addCase(deleteBatchRecordIngredient.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to delete batch record ingredient';
      });
  }
});

export const { clearError } = batchRecordIngredientsSlice.actions;
export default batchRecordIngredientsSlice.reducer;
